import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { RootState, useAppSelector } from '../store';
import { Credential, UserSettings, VoiceModel } from '../Types';
import toast from 'react-hot-toast';
import { voiceModelService } from '../Services/voiceModelService';
import { credentialsService } from '../Services/credentialsService';

const useVoiceModel = () => {
    const navigate = useNavigate();
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const location = useLocation();

    let { voiceModelIdentifier } = useParams();
    const [voiceModelSnapshot, setVoiceModelSnapshot] = useState<VoiceModel>();
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [credentials, setCredentials] = useState<Credential[]>([]);
    const [loadingState, setLoadingState] = useState({
        modelLoaded: false,
        llmModelLoaded: false,
        credentialsLoaded: false,
    });
    const [voiceModel, setVoiceModel] = useState<VoiceModel>({
        identifier: '',
        title: '',
        value: '',
        costs: 0.0, // Legacy field, not used for VoiceModels
        costTextInput: null,
        costTextOutput: null,
        costAudio: null,
        costAudioOutput: null,
        type: null,
        settings: {},
        image: '',
        credential: null,
    });

    const notifySuccess = useCallback(() => {
        toast.success(i18n.translate({ id: 'toast', sourceName: 'Model' }));
    }, [i18n]);

    const handleChangeModel = useCallback((key: keyof VoiceModel, value: any) => {
        setVoiceModel((prevModel) => ({
            ...prevModel,
            [key]: value,
        }));
    }, []);

    const updateVoiceModel = useCallback((updates: Partial<VoiceModel>) => {
        setVoiceModel((prevModel) => ({
            ...prevModel,
            ...updates,
        }));
    }, []);

    const handleImageChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files && e.target.files[0]) {
                const file = e.target.files[0];
                setImagePreview(URL.createObjectURL(file));
                handleChangeModel('image', file);
            }
        },
        [handleChangeModel],
    );

    const handleCredentialDropdownChange = useCallback(
        (value: string) => {
            const selectedCredential = credentials.find((credential) => credential.identifier === value);
            if (selectedCredential) {
                handleChangeModel('credential', selectedCredential);
            }
        },
        [credentials, handleChangeModel],
    );

    const handleRemoveImage = useCallback(() => {
        setImagePreview(null);
        handleChangeModel('image', null);
    }, [handleChangeModel]);

    const handleSave = useCallback(() => {
        if (!userSettings.isLoggedIn) return;
        const data = new FormData();
        data.append('title', voiceModel.title);
        data.append('value', voiceModel.value);
        if (voiceModel.costTextInput !== null && voiceModel.costTextInput !== undefined) {
            data.append('costTextInput', (voiceModel.costTextInput / 1000000).toString());
        }
        if (voiceModel.costTextOutput !== null && voiceModel.costTextOutput !== undefined) {
            data.append('costTextOutput', (voiceModel.costTextOutput / 1000000).toString());
        }
        if (voiceModel.costAudio !== null && voiceModel.costAudio !== undefined) {
            data.append('costAudio', (voiceModel.costAudio / 1000000).toString());
        }
        if (voiceModel.costAudioOutput !== null && voiceModel.costAudioOutput !== undefined) {
            data.append('costAudioOutput', (voiceModel.costAudioOutput / 1000000).toString());
        }
        if (voiceModel.type !== null && voiceModel.type !== undefined) {
            data.append('type', voiceModel.type);
        }
        if (voiceModel.credential) {
            data.append('credential', voiceModel.credential.identifier);
        }

        data.append('settings', JSON.stringify(voiceModel.settings));

        if (voiceModel.image instanceof File || voiceModel.image instanceof Blob) {
            const fileType = voiceModel.image.type.split('/')[1];
            const fileName = `upload.${fileType}`;
            data.append('image', new File([voiceModel.image], fileName, { type: voiceModel.image.type }));
        } else if (voiceModel.image && typeof voiceModel.image === 'string') {
            data.append('imageUrl', voiceModel.image);
        }
        // Note: Image removal is currently not supported. If needed, this should be implemented
        // by sending an empty string or a specific flag to indicate image removal.

        const type = voiceModelIdentifier === 'new' ? 'create' : 'update/' + voiceModelIdentifier;

        voiceModelService.saveModel(type, data).then((response: { data: VoiceModel }) => {
            voiceModelIdentifier === 'new' && navigate('/admin/voicemodels/' + response.data.identifier);
            setVoiceModelSnapshot(response.data);
            notifySuccess();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }, [voiceModel, voiceModelIdentifier, userSettings.isLoggedIn, navigate, notifySuccess]);

    useEffect(() => {
        if (!userSettings.isLoggedIn) return;

        credentialsService.getCredentials().then((response) => {
            setCredentials(response.data.data);
            setLoadingState((prevState) => ({ ...prevState, credentialsLoaded: true }));

            if (voiceModelIdentifier !== 'new' && voiceModelIdentifier !== undefined) {
                voiceModelService.getModel(voiceModelIdentifier).then((response: { data: VoiceModel }) => {
                    const modelData = {
                        ...response.data,
                        costTextInput: response.data.costTextInput
                            ? parseFloat((response.data.costTextInput * 1000000).toFixed(10))
                            : null,
                        costTextOutput: response.data.costTextOutput
                            ? parseFloat((response.data.costTextOutput * 1000000).toFixed(10))
                            : null,
                        costAudio: response.data.costAudio
                            ? parseFloat((response.data.costAudio * 1000000).toFixed(10))
                            : null,
                        costAudioOutput: response.data.costAudioOutput
                            ? parseFloat((response.data.costAudioOutput * 1000000).toFixed(10))
                            : null,
                    };
                    setVoiceModel(modelData);
                    setImagePreview(response.data.image);
                    setLoadingState((prevState) => ({ ...prevState, modelLoaded: true }));
                    setVoiceModelSnapshot(response.data);
                });
            } else {
                const duplicateData = (location.state as any)?.duplicateData;
                if (duplicateData) {
                    const modelData = {
                        ...duplicateData,
                        identifier: '',
                        costTextInput: duplicateData.costTextInput
                            ? parseFloat((duplicateData.costTextInput * 1000000).toFixed(10))
                            : null,
                        costTextOutput: duplicateData.costTextOutput
                            ? parseFloat((duplicateData.costTextOutput * 1000000).toFixed(10))
                            : null,
                        costAudio: duplicateData.costAudio
                            ? parseFloat((duplicateData.costAudio * 1000000).toFixed(10))
                            : null,
                        costAudioOutput: duplicateData.costAudioOutput
                            ? parseFloat((duplicateData.costAudioOutput * 1000000).toFixed(10))
                            : null,
                    };
                    setVoiceModel({ ...modelData, image: duplicateData.image ?? '' });
                    if (duplicateData.image) {
                        setImagePreview(duplicateData.image);
                    }
                    setVoiceModelSnapshot(modelData);
                }
                setLoadingState((prevState) => ({ ...prevState, modelLoaded: true }));
            }
        });
    }, [userSettings.isLoggedIn, voiceModelIdentifier, location.state]);

    const VoiceModelActions = useMemo(
        () => ({
            handleImageChange,
            handleSave,
            handleChangeModel,
            updateVoiceModel,
            handleRemoveImage,
            handleCredentialDropdownChange,
        }),
        [
            handleImageChange,
            handleSave,
            handleChangeModel,
            updateVoiceModel,
            handleRemoveImage,
            handleCredentialDropdownChange,
        ],
    );

    const handleNavigate = useCallback(
        (path: string) => {
            navigate(path);
        },
        [navigate],
    );

    return {
        VoiceModelActions,
        handleNavigate,
        voiceModelSnapshot,
        voiceModel,
        imagePreview,
        i18n,
        loadingState,
        voiceModelIdentifier,
        credentials,
    };
};

export default useVoiceModel;
